import { Navigate } from 'react-router-dom'
import { usePermissions, type PageSlug } from '@/hooks/use-permissions'

interface PageGateProps {
  page: PageSlug
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * Wraps a page component — redirects to dashboard if the user
 * doesn't have access to this page slug.
 */
export function PageGate({ page, children, fallback }: PageGateProps) {
  const { canAccess } = usePermissions()

  if (!canAccess(page)) {
    return fallback ? <>{fallback}</> : <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

interface AdminGateProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * Wraps admin-only content — hides or redirects non-admins.
 */
export function AdminGate({ children, fallback }: AdminGateProps) {
  const { isAdmin } = usePermissions()

  if (!isAdmin) {
    return fallback ? <>{fallback}</> : <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

interface PlatformAdminGateProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * Wraps platform-admin-only content (dev / testing pages).
 * Non-platform-admins get redirected to dashboard.
 */
export function PlatformAdminGate({ children, fallback }: PlatformAdminGateProps) {
  const { isPlatformAdmin } = usePermissions()

  if (!isPlatformAdmin) {
    return fallback ? <>{fallback}</> : <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
